import { useEffect, useState } from 'react'
import { client } from '../lib/sanity'
import ProjectCard from '../components/ProjectCard'
import FilterSelect from '../components/FilterSelect'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import { motion } from 'framer-motion'

export default function Projects() {
    const [projects, setProjects] = useState([])
    const [selectedCategory, setSelectedCategory] = useState('')
    const [selectedSubcategory, setSelectedSubcategory] = useState('')

    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(true)

    const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.12 } } }

    useEffect(() => {
        setLoading(true)
        client.fetch(`*[_type == "project"]|order(featured desc, title asc){
        title,
        description,
        url,
        artifactType,
        artifactUrl,
        "image": image.asset->url,
        category->{title},
        subcategory->{title},
        featured,
        techStack
      }`).then(data => {
            const truncated = data.map(p => ({
                ...p,
                featured: Boolean(p.featured),
                techStack: Array.isArray(p.techStack) ? p.techStack : [],
                description: p.description?.substring(0, 120) + (p.description?.length > 120 ? '...' : '')
            }))
            setProjects(truncated)
        }).finally(() => setLoading(false))
    }, [])

    const categories = [...new Set(projects.map(p => p.category?.title?.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    const subcategories = [...new Set(projects.map(p => p.subcategory?.title?.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b))

    const filteredProjects = projects.filter(p => {
        const matchCat = !selectedCategory || p.category?.title === selectedCategory
        const matchSub = !selectedSubcategory || p.subcategory?.title === selectedSubcategory
        const q = search.trim().toLowerCase()
        const matchSearch = !q || [
          p.title,
          p.description,
          p.category?.title,
          p.subcategory?.title,
          ...(Array.isArray(p.techStack) ? p.techStack : [])
        ].filter(Boolean).some(s => s.toLowerCase().includes(q))
        return matchCat && matchSub && matchSearch
    })

    const sortedProjects = [...filteredProjects].sort((a, b) => {
      const fa = a.featured ? 1 : 0
      const fb = b.featured ? 1 : 0
      if (fb - fa !== 0) return fb - fa
      return a.title.localeCompare(b.title)
    })

    return (
        <div className="min-h-screen bg-white dark:bg-zinc-950">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
                <div className="flex flex-wrap gap-3 mb-12 items-center">
                    <FilterSelect value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="w-auto">
                        <option value="">All Categories</option>
                        {categories.map((cat, i) => (
                            <option key={i} value={cat}>{cat}</option>
                        ))}
                    </FilterSelect>
                    <FilterSelect value={selectedSubcategory} onChange={e => setSelectedSubcategory(e.target.value)} className="w-auto">
                        <option value="">All Subcategories</option>
                        {subcategories.map((sub, i) => (
                            <option key={i} value={sub}>{sub}</option>
                        ))}
                    </FilterSelect>
                    <Input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search projects"
                        className="min-w-[220px] w-auto"
                    />
                    <Button
                        variant="secondary"
                        onClick={() => { setSelectedCategory(''); setSelectedSubcategory(''); setSearch('') }}
                    >
                        Reset
                    </Button>
                    <span className="text-sm font-mono text-zinc-600 dark:text-zinc-300 ml-2">{filteredProjects.length} result{filteredProjects.length === 1 ? '' : 's'}</span>
                </div>

                {loading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="h-[430px] border-2 border-black dark:border-white bg-white dark:bg-zinc-950 animate-pulse" />
                        ))}
                    </div>
                )}
                {!loading && filteredProjects.length === 0 && (
                    <div className="text-center text-zinc-700 dark:text-zinc-300">No projects match these filters.</div>
                )}

                <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                    variants={container}
                    initial="hidden"
                    animate={loading ? 'hidden' : 'show'}
                >
                    {sortedProjects.map((project, idx) => (
                        <div key={idx} className={idx === 0 && project.featured ? 'sm:col-span-2 lg:col-span-3' : ''}>
                            <ProjectCard {...project} featured={project.featured} techStack={project.techStack} large={idx === 0 && project.featured} />
                        </div>
                    ))}
                </motion.div>
            </div>
        </div>
    )
}